import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BEResponse } from '@qtek/core/api-core';

import {
  BaseWebsocketMessage,
  GetWebsocketMessageRequest,
  InsWebsocketMessageRequest,
  QueryWebsocketMessageRequest,
  SubWebsocketMessageRequest,
  UpdWebsocketMessageRequest,
  WebsocketEntTypes,
  WebSocketService,
  WSEntityManager,
} from '@qtek/core/websockets-core';
import {
  HTTP_BASE_URL,
  OrganizationLicenseUpdatePayload,
} from '@qtek/shared/models';
import { map, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class OrganizationsService {
  private websocketService = inject(WebSocketService);
  protected http = inject(HttpClient);
  private httpBaseUrl = inject(HTTP_BASE_URL);

  entityName: WebsocketEntTypes = 'acn';
  wsManager = new WSEntityManager(this.websocketService, this.entityName);

  subscribe(
    payload: Omit<SubWebsocketMessageRequest, 'ent' | 'op'>
  ): Observable<BaseWebsocketMessage> {
    return this.wsManager.subscribeEntity(payload, true);
  }

  createOrganization(payload: Omit<InsWebsocketMessageRequest, 'ent' | 'op'>) {
    this.wsManager.create(payload);
  }

  updateOrganization(payload: Omit<UpdWebsocketMessageRequest, 'ent' | 'op'>) {
    this.wsManager.update(payload);
  }

  getOrganizations(payload: Omit<QueryWebsocketMessageRequest, 'ent' | 'op'>) {
    this.wsManager.query(payload);
  }

  getOrganizationDetails(
    payload: Omit<SubWebsocketMessageRequest, 'ent' | 'op'>,
    messageFilter?: (message: any) => boolean
  ): Observable<BaseWebsocketMessage> {
    return this.wsManager.subscribeEntity(payload, false, messageFilter);
  }

  getOrganizationLicenses(): Observable<any> {
    return this.http.get<BEResponse<any>>(
      this.httpBaseUrl + '/api/v1/service/4sale/synply',
      {
        withCredentials: true,
      }
    );
  }

  deleteOrganizationAccount(acnId: string) {
    return this.http.delete(
      `${this.httpBaseUrl}/api/v1/model/${this.entityName}/${acnId}?tp=force`,
      { withCredentials: true }
    );
  }

  getOrganizationById(payload: Omit<GetWebsocketMessageRequest, 'ent' | 'op'>) {
    this.wsManager.get(payload);
  }

  updateOrganizationLicense(
    payload: OrganizationLicenseUpdatePayload
  ): Observable<any> {
    return this.http.post(this.httpBaseUrl + '/api/v1/model/licins', payload, {
      withCredentials: true,
    });
  }

  updateOrganizationUser(userId: string, payload: any): Observable<any> {
    return this.http.put(
      `${this.httpBaseUrl}/api/v1/admin/update-user/${userId}`,
      payload,
      { withCredentials: true }
    );
  }

  resetUserPassword(userId: string): Observable<any> {
    return this.http.post(
      `${this.httpBaseUrl}/api/v1/admin/reset-password-email/${userId}`,
      {},
      { withCredentials: true }
    );
  }

  resetUserPasswordLink(userId: string): Observable<any> {
    return this.http.post(
      `${this.httpBaseUrl}/api/v1/admin/reset-password-link/${userId}`,
      {},
      { withCredentials: true }
    );
  }

  generateOnBehalfUrl(userId: string): Observable<string> {
    return this.http
      .get(
        `${this.httpBaseUrl}/api/v1/admin/generate-on-behalf-token/${userId}`,
        { withCredentials: true }
      )
      .pipe(map((r: any) => r.res));
  }

  loginAsAnotherUser(fullUrl: string): Observable<any> {
    return this.http.post(
      fullUrl,
      {},
      {
        withCredentials: true,
      }
    );
  }

  loginAsAnotherUserByToken(token: string) {
    const headers = new HttpHeaders().append(
      'X-Skip-Revalidate-Error-Handling',
      ''
    );
    return this.http.post(
      `${this.httpBaseUrl}/api/v1/admin/login-as-another-user/${encodeURIComponent(token)}`,
      {},
      { withCredentials: true, headers }
    );
  }

  updateOrganizationName(acnId: string, name: string): Observable<any> {
    return this.http.put(
      `${this.httpBaseUrl}/api/v1/admin/update-organization/${acnId}`,
      { name: [name] },
      { withCredentials: true }
    );
  }

  updateInvestorProfileVisibility(
    acnId: string,
    visibility: 'pablic' | 'none'
  ): Observable<any> {
    return this.http.put(
      `${this.httpBaseUrl}/api/v1/admin/update-organization/${acnId}`,
      { investorProfileVisibilityLevel: visibility },
      { withCredentials: true }
    );
  }

  updateFeatureStage(acnId: string, featureStg: string): Observable<any> {
    return this.http.put(
      `${this.httpBaseUrl}/api/v1/admin/update-organization/${acnId}`,
      { featureStg: [featureStg] },
      { withCredentials: true }
    );
  }

  addOrganizationDomain(acnId: string, domain: string): Observable<any> {
    return this.http.post(
      `${this.httpBaseUrl}/api/v1/admin/add-domain/${acnId}`,
      { id: [domain] },
      { withCredentials: true }
    );
  }

  removeLoginBlock(email: string): Observable<any> {
    return this.http.post(
      `${this.httpBaseUrl}/api/v1/admin/remove-login-block/${encodeURIComponent(email)}`,
      {},
      { withCredentials: true }
    );
  }
}
